import { resolveHomeHttpClient } from '~/domains/banking/shared/infra/resolve-home-http-client'
import { type StoneAccount } from '~/domains/platform/infra/deus-ex-machina/types'
import { timeMachine } from '~/domains/platform/lib/time-machine'
import { useQueryRequest } from '~/ui/hooks/utils/service'

import { type GetHomeBff } from '../../types'

import { useIsBffHomeKaravelaEnabled } from './use-is-bff-home-karavela-enabled'

interface GetHome {
  document?: string
  accountId?: string
  globalId?: string
  affiliationKey?: string
  stoneAccount?: StoneAccount
  isKaravelaEnabled?: boolean
}

export async function getHome({
  document,
  accountId,
  globalId,
  affiliationKey,
  stoneAccount,
  isKaravelaEnabled
}: GetHome) {
  const isOneStone = stoneAccount?.situation === 'one_stone'

  const user = {
    plataform: 'web',
    document,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
  }

  const salesFilter = {
    startDate: timeMachine(new Date()).subtractDay(7).toISOString(),
    endDate: timeMachine(new Date()).toISOString()
  }

  const httpClient = resolveHomeHttpClient({ isOneStone, isKaravelaEnabled })

  if (isOneStone) {
    return httpClient<GetHomeBff>({
      url: '/bff-home/web',
      method: 'POST',
      data: {
        user,
        legacyIdentifier: {
          global: {
            id: globalId,
            affiliationKey: affiliationKey,
            account: {
              id: accountId
            }
          }
        },
        stoneAccount: {
          id: stoneAccount?.id,
          paymentProfiles: stoneAccount?.paymentProfiles.map(item => item.id)
        },
        salesFilter
      }
    })
  }

  return httpClient<GetHomeBff>({
    url: '/bff-home/web',
    method: 'POST',
    data: {
      user,
      global: {
        id: globalId,
        account: {
          id: accountId
        },
        affiliationKey: affiliationKey
      },
      salesFilter
    }
  })
}

export function useGetHome({ document, accountId, globalId, affiliationKey, stoneAccount }: GetHome) {
  const { isKaravelaEnabled, isKaravelaLoading } = useIsBffHomeKaravelaEnabled()

  const { data: response, ...restQuery } = useQueryRequest<GetHomeBff>(
    [
      'getBffHomeShell',
      document,
      accountId,
      globalId,
      affiliationKey,
      { situation: stoneAccount?.situation },
      isKaravelaEnabled
    ],
    () => getHome({ document, accountId, globalId, affiliationKey, stoneAccount, isKaravelaEnabled }),
    { enabled: !isKaravelaLoading }
  )
  const data = response?.data || ({} as GetHomeBff)

  return {
    data: data,
    alerts: data.components?.alerts,
    cardsFooter: data.components?.cardsFooter,
    menu: { data: data.components?.sidebar?.menu, id: data.components?.sidebar.id },
    modal: data.components?.modal,
    teamRoleBar: data.components?.sidebar?.teamRoleBar,
    balance: data.components?.balance,
    header: data.components?.header,
    statement: data.components?.statement,
    salesCard: data.components?.salesCard,
    receivablesCard: data.components?.receivablesCard,
    creditBanner: data.components?.creditBanner,
    ...restQuery,
    isLoading: isKaravelaLoading || restQuery.isLoading
  }
}
